import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  PlusIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  ArrowLeftIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

// Importer le formulaire de client
import CustomerForm from '../../components/customers/CustomerForm';
import { useAppData } from '../../contexts/AppDataContext';

// Fonction utilitaire pour formater les montants en DA de manière sûre
const formatDA = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('fr-DZ') : '0';
};

// Helper functions for customer data - shared between components
const getCustomerInitials = (customer) => {
  const firstName = customer?.firstName || '';
  const lastName = customer?.lastName || '';
  return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase();
};

const getCustomerFullName = (customer) => {
  const firstName = customer?.firstName || '';
  const lastName = customer?.lastName || '';
  return `${firstName} ${lastName}`.trim() || 'Client inconnu';
};


const CustomersList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const { customers: contextCustomers = [], deleteCustomer, fetchAllData, getCustomers } = useAppData();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manual refresh function
  const handleManualRefresh = async () => {
    console.log('🔄 Manual refresh triggered');
    setLoading(true);
    
    try {
      // First try to get fresh data from context function
      const freshCustomers = getCustomers();
      if (freshCustomers && freshCustomers.length > 0) {
        setCustomers(freshCustomers);
        console.log('📊 Refreshed from getCustomers():', freshCustomers.length);
      } else {
        // Fallback to localStorage
        const savedCustomers = localStorage.getItem('stockpro_customers');
        if (savedCustomers) {
          const parsedCustomers = JSON.parse(savedCustomers);
          setCustomers(parsedCustomers);
          console.log('💾 Refreshed from localStorage:', parsedCustomers.length);
        }
      }
    } catch (error) {
      console.error('❌ Error during manual refresh:', error);
    }
    
    setLoading(false);
  };
  
  // Exemple de données de clients
  const sampleCustomers = [
    {
      id: 'CUST-001',
      firstName: 'Ahmed',
      lastName: 'Benali',
      company: 'ABC Trading SARL',
      email: 'ahmed.benali@example.com',
      phone: '+213 555 123 456',
      address: '15 Rue Mohamed Khemisti, Alger',
      wilaya: 'Alger',
      type: 'enterprise',
      createdAt: '2023-02-15',
      lastTransaction: '2023-07-10',
      totalTransactions: 12,
      totalSpent: 145000
    },
    {
      id: 'CUST-002',
      firstName: 'Samira',
      lastName: 'Hadj',
      company: '',
      email: 'samira.hadj@example.com',
      phone: '+213 555 789 012',
      address: '7 Boulevard Zighoud Youcef, Constantine',
      wilaya: 'Constantine',
      type: 'individual',
      createdAt: '2023-03-20',
      lastTransaction: '2023-07-18',
      totalTransactions: 4,
      totalSpent: 28500
    },
    {
      id: 'CUST-003',
      firstName: 'Karim',
      lastName: 'Ziani',
      company: 'Ziani Import-Export',
      email: 'karim@zianigroup.dz',
      phone: '+213 555 456 789',
      address: '22 Avenue Hassiba Ben Bouali, Oran',
      wilaya: 'Oran',
      type: 'enterprise',
      createdAt: '2023-01-10',
      lastTransaction: '2023-07-05',
      totalTransactions: 18,
      totalSpent: 230000
    },
    {
      id: 'CUST-004',
      firstName: 'Amina',
      lastName: 'Belkacem',
      company: '',
      email: 'amina.b@example.com',
      phone: '+213 555 234 567',
      address: '3 Rue des Frères Bouadou, Tizi Ouzou',
      wilaya: 'Tizi Ouzou',
      type: 'individual',
      createdAt: '2023-04-05',
      lastTransaction: '2023-06-28',
      totalTransactions: 2,
      totalSpent: 12500
    },
    {
      id: 'CUST-005',
      firstName: 'Nabil',
      lastName: 'Messaoudi',
      company: 'Tech Solutions Algérie',
      email: 'n.messaoudi@techsolutions.dz',
      phone: '+213 555 987 654',
      address: '45 Boulevard Mohamed V, Alger',
      wilaya: 'Alger',
      type: 'enterprise',
      createdAt: '2023-02-28',
      lastTransaction: '2023-07-15',
      totalTransactions: 8,
      totalSpent: 175000
    },
    {
      id: 'CUST-006',
      firstName: 'Leila',
      lastName: 'Benabdellah',
      company: '',
      email: 'leila.b@example.com',
      phone: '+213 555 345 678',
      address: '17 Rue Larbi Tébessi, Annaba',
      wilaya: 'Annaba',
      type: 'individual',
      createdAt: '2023-05-12',
      lastTransaction: '2023-07-08',
      totalTransactions: 3,
      totalSpent: 9800
    },
  ];

  // Charger les données au chargement du composant et les maintenir à jour
  useEffect(() => {
    console.log('🔄 CustomersPage useEffect triggered', {
      contextCustomersCount: contextCustomers?.length || 0,
      contextCustomersSample: contextCustomers?.slice(0, 2),
      trigger: 'context customers changed'
    });
    
    // Load customers with real-time data
    const loadCustomers = () => {
      setLoading(true);
      
      if (contextCustomers && contextCustomers.length > 0) {
        setCustomers(contextCustomers);
        console.log('📊 Loaded customers from context:', {
          count: contextCustomers.length,
          customers: contextCustomers.map(c => ({
            id: c.id,
            firstName: c.firstName,
            lastName: c.lastName,
            email: c.email
          }))
        });
      } else {
        // Check localStorage directly as fallback
        const savedCustomers = localStorage.getItem('stockpro_customers');
        if (savedCustomers) {
          try {
            const parsedCustomers = JSON.parse(savedCustomers);
            setCustomers(parsedCustomers);
            console.log('💾 Loaded customers from localStorage:', {
              count: parsedCustomers.length,
              customers: parsedCustomers.map(c => ({
                id: c.id,
                firstName: c.firstName,
                lastName: c.lastName,
                email: c.email
              }))
            });
          } catch (error) {
            console.error('❌ Error parsing customers from localStorage:', error);
            setCustomers(sampleCustomers); // Fallback to sample data if all else fails
          }
        } else {
          setCustomers(sampleCustomers); // Fallback to sample data if no customers in storage
          console.log('🎭 Using sample customers data as fallback');
        }
      }
      
      setLoading(false);
    };
    
    loadCustomers();
  }, [contextCustomers]); // Depend on contextCustomers to update when data changes

  // Also refresh when the page becomes visible (user returns from adding customer)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('📱 Page became visible, refreshing customers...');
        handleManualRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleManualRefresh]);

  // Filtrer les clients
  const filteredCustomers = customers.filter(customer => {
    const fullName = getCustomerFullName(customer).toLowerCase();
    const matchesSearch = searchTerm === '' || 
      fullName.includes(searchTerm.toLowerCase()) ||
      (customer.company && customer.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.id && customer.id.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'all' || customer.type === filterType;
    
    return matchesSearch && matchesType;
  });

  // Stats sur les clients
  const customerStats = [
    {
      title: 'Total des clients',
      value: customers.length,
      icon: UserCircleIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Entreprises',
      value: customers.filter(c => c.type === 'enterprise').length,
      icon: BuildingOfficeIcon,
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'Particuliers',
      value: customers.filter(c => c.type === 'individual').length,
      icon: UserCircleIcon,
      color: 'from-green-500 to-green-600',
    }
  ];

  // Gérer la visualisation d'un client
  const handleViewCustomer = (id) => {
    navigate(`/customers/${id}`);
  };

  // Gérer la modification d'un client
  const handleEditCustomer = (id) => {
    navigate(`/customers/${id}/edit`);
  };

  // Gérer la suppression d'un client
  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        // Appeler la fonction deleteCustomer du contexte pour supprimer le client
        const success = await deleteCustomer(id);
        
        if (success) {
          // Mettre à jour la liste locale des clients
          setCustomers(prevCustomers => prevCustomers.filter(customer => customer.id !== id));
          toast.success('Client supprimé avec succès');
          
          // Rafraîchir les données globales
          await fetchAllData();
        } else {
          toast.error('Erreur lors de la suppression du client');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression du client:', error);
        toast.error('Erreur lors de la suppression du client');
      }
    }
  };

  // Export and import handlers removed

  // Gérer l'ajout d'un nouveau client
  const handleAddCustomer = () => {
    navigate('/customers/new');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête de la page */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Clients</h1>
          <p className="text-white/60 mt-2">Gérez votre portefeuille clients</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleManualRefresh}
            disabled={loading}
            className="bg-white/10 backdrop-blur-lg text-white px-4 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center space-x-2 border border-white/20 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </button>
          <button 
            onClick={handleAddCustomer}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nouveau client</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {customerStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Barre de recherche et filtres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-grow">
            <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Rechercher un client par nom, entreprise ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex space-x-3">
            <button 
              onClick={() => setFilterOpen(!filterOpen)}
              className={`px-4 py-2.5 rounded-xl flex items-center space-x-2 transition-colors ${
                filterOpen ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
              <span>Filtres</span>
            </button>
          </div>
        </div>

        {/* Filtres */}
        {filterOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-6 pt-6 border-t border-white/10"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-white/70 mb-2">
                  Type de client
                </label>
                <select
                  id="type"
                  className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">Tous les types</option>
                  <option value="enterprise">Entreprise</option>
                  <option value="individual">Particulier</option>
                </select>
              </div>
              <div>
                <label htmlFor="wilaya" className="block text-sm font-medium text-white/70 mb-2">
                  Wilaya
                </label>
                <select
                  id="wilaya"
                  className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue="all"
                >
                  <option value="all">Toutes les wilayas</option>
                  {Array.from(new Set(customers.map(c => c.wilaya))).sort().map(wilaya => (
                    <option key={wilaya} value={wilaya}>{wilaya}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sortBy" className="block text-sm font-medium text-white/70 mb-2">
                  Trier par
                </label>
                <select
                  id="sortBy"
                  className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue="name"
                >
                  <option value="name">Nom (A-Z)</option>
                  <option value="name_desc">Nom (Z-A)</option>
                  <option value="recent">Plus récents</option>
                  <option value="oldest">Plus anciens</option>
                  <option value="transactions">Plus grand nombre de transactions</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => {
                  toast.success('Filtres appliqués');
                  setFilterOpen(false);
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl transition-colors hover:from-blue-700 hover:to-purple-700 font-medium"
              >
                Appliquer les filtres
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Liste des clients */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {filteredCustomers.map((customer) => (
          <motion.div 
            key={customer.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-colors relative group"
          >
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex space-x-1">
                <button 
                  onClick={() => handleViewCustomer(customer.id)} 
                  className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors" 
                  title="Voir"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleEditCustomer(customer.id)} 
                  className="p-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors" 
                  title="Modifier"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDeleteCustomer(customer.id)} 
                  className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors" 
                  title="Supprimer"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-12 w-12 bg-gradient-to-r from-blue-600/30 to-purple-600/30 rounded-full flex items-center justify-center text-lg text-white font-medium">
                {getCustomerInitials(customer)}
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-white truncate">
                  {getCustomerFullName(customer)}
                </h3>
                <div className="text-sm text-white/60">
                  {customer.type === 'enterprise' ? (
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                      <span>{customer.company || 'Nom de société non renseigné'}</span>
                    </div>
                  ) : (
                    <span className="text-white/50">Particulier</span>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-start text-white/70">
                <EnvelopeIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm break-all">{customer.email || 'Email non renseigné'}</span>
              </div>
              <div className="flex items-center text-white/70">
                <PhoneIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="text-sm">{customer.phone || 'Téléphone non renseigné'}</span>
              </div>
              <div className="flex items-start text-white/70">
                <MapPinIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{customer.wilaya || 'Localisation non renseignée'}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 flex justify-between">
              <div>
                <div className="text-xs text-white/50">Transactions</div>
                <div className="text-sm font-medium text-white">{customer.totalTransactions}</div>
              </div>
              <div>
                <div className="text-xs text-white/50">Total des achats</div>
                <div className="text-sm font-medium text-white">{formatDA(customer.totalSpent)} DA</div>
              </div>
            </div>
          </motion.div>
        ))}

        {filteredCustomers.length === 0 && (
          <div className="col-span-full text-center py-12 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
            <UserCircleIcon className="h-16 w-16 text-white/30 mx-auto" />
            <h3 className="text-xl font-medium text-white mt-4 mb-2">Aucun client trouvé</h3>
            <p className="text-white/60 mb-6">Aucun client ne correspond à vos critères de recherche.</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Vue détaillée d'un client
const CustomerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesContent, setNotesContent] = useState('');
  // Add the transactions state at the top level
  const [transactions, setTransactions] = useState([]);
  const { customers: contextCustomers = [], sales = [], getCustomerById, updateCustomer } = useAppData();
  
  // Load customer data
  useEffect(() => {
    const loadCustomerData = async () => {
      setLoading(true);
      
      try {
        // Try to get from context first
        let customerData = getCustomerById ? await getCustomerById(id) : null;
        
        // If not found in context, check localStorage
        if (!customerData) {
          const savedCustomers = localStorage.getItem('stockpro_customers');
          if (savedCustomers) {
            const parsedCustomers = JSON.parse(savedCustomers);
            customerData = parsedCustomers.find(c => c.id === id);
          }
        }
        
        // If still not found, check context array directly
        if (!customerData && contextCustomers && contextCustomers.length > 0) {
          customerData = contextCustomers.find(c => c.id === id);
        }
        
        if (customerData) {
          setCustomer(customerData);
          setError(null);
        } else {
          setError('Client introuvable');
        }
      } catch (err) {
        console.error('Error loading customer data:', err);
        setError('Erreur lors du chargement des données du client');
      }
      
      setLoading(false);
    };
    
    loadCustomerData();
  }, [id, contextCustomers, getCustomerById]);

  // Load transactions data
  useEffect(() => {
    const loadTransactions = async () => {
      if (!customer) return;
      
      try {
        // Get sales data related to this customer
        const customerSales = sales.filter(sale => sale.customerId === customer.id);
        
        // Map sales to transactions format
        const customerTransactions = customerSales.map(sale => ({
          id: sale.id,
          date: sale.date || sale.createdAt,
          type: 'Facture',
          reference: sale.reference || `FV-${sale.id}`,
          amount: parseFloat(sale.total) || 0,
          status: sale.status || 'paid'
        }));
        
        // Sort by date, most recent first
        customerTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        if (customerTransactions.length > 0) {
          setTransactions(customerTransactions);
        } else {
          // Fallback to empty state or sample data if needed
          setTransactions([]);
        }
      } catch (error) {
        console.error('Error loading customer transactions:', error);
        setTransactions([]);
      }
    };
    
    loadTransactions();
  }, [customer, sales]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-white">{error || 'Client non trouvé'}</h2>
        <button 
          onClick={() => navigate('/customers')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/customers')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-white">{getCustomerFullName(customer)}</h1>
              <span className={`ml-3 px-3 py-1 text-xs rounded-full ${
                customer.type === 'enterprise' 
                  ? 'bg-purple-500/30 text-purple-300'
                  : 'bg-blue-500/30 text-blue-300'
              }`}>
                {customer.type === 'enterprise' ? 'Entreprise' : 'Particulier'}
              </span>
            </div>
            <p className="text-white/60 mt-1">
              {customer.type === 'enterprise' ? customer.company : `Client depuis ${new Date(customer.createdAt).toLocaleDateString('fr-FR')}`}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => navigate(`/customers/${customer.id}/edit`)}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center space-x-2 transition-colors"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Modifier</span>
          </button>
          <button 
            onClick={() => {
              if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
                toast.success('Client supprimé avec succès');
                navigate('/customers');
              }
            }}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl flex items-center space-x-2 transition-colors"
          >
            <TrashIcon className="h-5 w-5" />
            <span>Supprimer</span>
          </button>
        </div>
      </div>

      {/* Contenu du client */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Coordonnées */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">Coordonnées</h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-white/60 text-sm">Email</p>
                <p className="text-white mt-1">{customer.email || 'Non renseigné'}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Téléphone</p>
                <p className="text-white mt-1">{customer.phone || 'Non renseigné'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-white/60 text-sm">Adresse</p>
                <p className="text-white mt-1">
                  {customer.address && `${customer.address}, `}
                  {customer.city && `${customer.city} `}
                  {customer.postalCode && customer.postalCode}
                </p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Wilaya</p>
                <p className="text-white mt-1">{customer.wilaya || 'Non renseignée'}</p>
              </div>
            </div>
          </div>
          
          {/* Informations fiscales pour les entreprises */}
          {customer.type === 'enterprise' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Informations fiscales</h2>
              
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-white/60 text-sm">NIF</p>
                  <p className="text-white mt-1 font-mono">{customer.nif || 'Non renseigné'}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">RC</p>
                  <p className="text-white mt-1 font-mono">{customer.rc || 'Non renseigné'}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">Article d'Imposition</p>
                  <p className="text-white mt-1 font-mono">{customer.ai || 'Non renseigné'}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Transactions récentes */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Transactions récentes</h2>
              
              <button
                onClick={() => navigate('/sales/new', { state: { customerId: customer.id } })}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm hover:opacity-90 transition-opacity flex items-center space-x-1"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Nouvelle transaction</span>
              </button>
            </div>
            
            {transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Référence</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-white/70">Montant</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-white/70">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {transactions.map(transaction => (
                      <tr key={transaction.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-white">
                          {new Date(transaction.date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-white">
                          {transaction.type}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-white font-mono">
                          {transaction.reference}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-white text-right font-medium">
                          {formatDA(transaction.amount)} DA
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            transaction.status === 'paid' ? 'bg-green-500/30 text-green-300' :
                            transaction.status === 'pending' ? 'bg-yellow-500/30 text-yellow-300' :
                            'bg-red-500/30 text-red-300'
                          }`}>
                            {transaction.status === 'paid' ? 'Payée' :
                             transaction.status === 'pending' ? 'En attente' : 'Annulée'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 space-y-4">
                <div className="bg-white/5 inline-flex rounded-full p-3">
                  <svg className="h-10 w-10 text-white/40" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/60 mb-3">Aucune transaction pour ce client.</p>
                  <button 
                    onClick={() => navigate('/sales/new', { state: { customerId: customer.id } })}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Créer une vente
                  </button>
                </div>
              </div>
            )}
            
            {transactions.length > 0 && (
              <div className="mt-4 text-right">
                <button 
                  onClick={() => navigate('/sales', { state: { filterCustomerId: customer.id } })}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                >
                  Voir toutes les transactions
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Colonne secondaire */}
        <div className="space-y-6">
          {/* Résumé */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">Résumé</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-white/60">Total des transactions</p>
                <p className="text-white font-medium">{customer.totalTransactions || 0}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-white/60">Montant total</p>
                <p className="text-white font-medium">{formatDA(customer.totalSpent || 0)} DA</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-white/60">Date de création</p>
                <p className="text-white">{customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('fr-FR') : 'N/A'}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-white/60">Dernière transaction</p>
                <p className="text-white">{customer.lastTransaction ? new Date(customer.lastTransaction).toLocaleDateString('fr-FR') : 'Aucune'}</p>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button 
                onClick={() => navigate('/sales/new', { state: { customerId: customer.id, type: 'invoice' } })}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm"
              >
                Nouvelle facture
              </button>
              <button 
                onClick={() => navigate('/sales/new', { state: { customerId: customer.id, type: 'delivery' } })}
                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors text-sm"
              >
                Nouveau bon
              </button>
            </div>
          </div>
          
          {/* Notes */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-4">Notes</h2>
            
            {/* State for notes editing */}
            {editingNotes ? (
              <>
                <textarea 
                  className="w-full p-4 bg-white/5 rounded-xl text-white/80 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                  value={notesContent}
                  onChange={(e) => setNotesContent(e.target.value)}
                  placeholder="Ajouter des notes sur ce client..."
                />
                
                <div className="mt-4 flex space-x-3">
                  <button 
                    onClick={async () => {
                      try {
                        // Update customer notes
                        await updateCustomer(customer.id, { notes: notesContent });
                        toast.success('Notes mises à jour avec succès');
                        setEditingNotes(false);
                        // Update the local state
                        setCustomer(prev => ({...prev, notes: notesContent}));
                      } catch (error) {
                        console.error('Erreur lors de la mise à jour des notes:', error);
                        toast.error('Erreur lors de la mise à jour des notes');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm flex-1"
                  >
                    Enregistrer
                  </button>
                  <button 
                    onClick={() => {
                      setEditingNotes(false);
                      setNotesContent(customer.notes || '');
                    }}
                    className="px-4 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors text-sm flex-1"
                  >
                    Annuler
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-white/80 whitespace-pre-wrap">
                    {customer.notes || 'Aucune note pour ce client.'}
                  </p>
                </div>
                
                <button 
                  onClick={() => {
                    setEditingNotes(true);
                    setNotesContent(customer.notes || '');
                  }}
                  className="mt-4 text-sm text-blue-400 hover:text-blue-300"
                >
                  Modifier les notes
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomersPage = () => {
  return (
    <Routes>
      <Route index element={<CustomersList />} />
      <Route path="new" element={<CustomerForm />} />
      <Route path=":id" element={<CustomerDetail />} />
      <Route path=":id/edit" element={<CustomerForm />} />
    </Routes>
  );
};

export default CustomersPage; 